import {
  ContentData,
  FileExtensions,
  IUploadConfig,
  UploadRequestMetaData,
  UploadScenarios,
} from 'interfaces/file-uploader';
import axios from 'axios';
import { request } from './request';

export enum FileType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  FOLDER = 'folder',
  FONT = 'font',
  COMPRESSED_FILE = 'compressed-file',
}

const mbSize = 1000000;

const videos = [
  FileExtensions.mp4,
  FileExtensions.flv,
  FileExtensions.mkv,
  FileExtensions.webm,
  FileExtensions.mov,
  FileExtensions.wmv,
];
const images = [FileExtensions.jpg, FileExtensions.jpeg, FileExtensions.png, FileExtensions.gif];
const documents = [FileExtensions.pdf];
const fonts = [FileExtensions.woff, FileExtensions.woff2];
const compressItems = [FileExtensions.zip];

export const uploadConfig = new Map<number, IUploadConfig>([
  [
    UploadScenarios.expertVideo,
    { allowedExtensions: [...videos], sizeLimit: 500 * mbSize },
  ],
  [
    UploadScenarios.expertProfile,
    { allowedExtensions: [...images], sizeLimit: 30 * mbSize },
  ],
  [
    UploadScenarios.expertSponsor,
    { allowedExtensions: [...images], sizeLimit: 500 * mbSize },
  ],
  [
    UploadScenarios.expertBadge,
    { allowedExtensions: [...images], sizeLimit: 30 * mbSize },
  ],
  [
    UploadScenarios.expertContent,
    {
      allowedExtensions: [...images, ...videos, ...documents],
      sizeLimit: 3072 * mbSize, // allow 3 GB
    },
  ],
  [
    UploadScenarios.articleBanner,
    { allowedExtensions: [...images], sizeLimit: 100 * mbSize },
  ],
  [
    UploadScenarios.sessionBanner,
    { allowedExtensions: [...images], sizeLimit: 30 * mbSize },
  ],
  [
    UploadScenarios.sessionFeedback,
    { allowedExtensions: [...videos], sizeLimit: 9000 * mbSize }, // allow 9 GB
  ],
  [
    UploadScenarios.groupBanner,
    { allowedExtensions: [...images], sizeLimit: 100 * mbSize },
  ],
  [
    UploadScenarios.formBadge,
    { allowedExtensions: [...images], sizeLimit: 30 * mbSize },
  ],
  [
    UploadScenarios.generateThumbnail,
    { allowedExtensions: [FileExtensions.jpeg], sizeLimit: 2 * mbSize },
  ],
]);

export const extensionToType = new Map<string, string>([]);
videos.forEach((video) => {
  extensionToType.set(video, FileType.VIDEO);
});
images.forEach((image) => {
  extensionToType.set(image, FileType.IMAGE);
});
documents.forEach((document) => {
  extensionToType.set(document, FileType.DOCUMENT);
});
fonts.forEach((font) => {
  extensionToType.set(font, FileType.FONT);
});
compressItems.forEach((item) => {
  extensionToType.set(item, FileType.COMPRESSED_FILE);
});

/**
 * @param  {any} e  File change event
 * @param  {string} url Current url of the content data object that needs to be deleted
 * @return void
 */
export const handleContentDataDelete = async (
  url: string,
  scenario: UploadScenarios,
): Promise<void> => {
  const content: ContentData = {
    action: 'delete',
    type: '',
    file: '',
    fileName: '',
    url,
    extension: '',
    category: '',
  };
  if (!url || !scenario) {
    return;
  }
  const UPLOAD_PATH: UploadRequestMetaData = { path: '', method: 'POST' };
  UPLOAD_PATH.path = '/expert/upload';
  if (scenario === UploadScenarios.expertVideo) {
    content.type = 'video';
  } else if (
    scenario === UploadScenarios.expertProfile
    || scenario === UploadScenarios.expertSponsor
  ) {
    content.type = 'image';
  } else {
    return;
  }
  await request(UPLOAD_PATH, content, true, true);
};

export const getFileTypeFromUrl = (url: string): string => {
  const extention = (url.split('.').at(-1) as string).toLowerCase();
  return extensionToType.get(`.${extention}`) as string;
};

export const getFileDataFromUrl = async (url: string): Promise<any> => new Promise(
  (resolve, reject) => {
    axios({
      url,
      method: 'head',
      withCredentials: false,
    }).then((res) => {
      const contentType = res.headers['content-type'];
      const fileType = contentType?.split('/').pop();
      if (contentType && fileType) {
        const fileCategory = extensionToType.get(`.${fileType.toLowerCase()}`) as string;
        resolve({
          fileType: `.${fileType.toUpperCase()}`,
          fileCategory,
          fileName: url?.split('/').pop() ?? '-',
        });
      } else {
        reject(new Error('URL is invalid'));
      }
    }).catch((e) => {
      reject(new Error('URL is invalid'));
    });
  },
);

export const urltoFile = async (url: string, filename: string, mimeType: string) => (fetch(url)
  .then((res) => res.arrayBuffer())
  .then((buf) => new File([buf], filename, { type: mimeType }))
);

export const urlExists = (url: string) => {
  try {
    const http = new XMLHttpRequest();
    http.open('HEAD', url, false);
    http.send();
    return [200].includes(http.status);
  } catch (error) {
    return false;
  }
};
